// Zone de dispatch d'une course (demande Sylvain 30/09/2026) : Paris part dans
// les groupes WhatsApp par type de véhicule, chaque grande ville de province a
// son prestataire unique. Calcul par les coordonnées quand la course en a
// (pickup.lat/lon posés au géocodage), sinon par le texte des adresses.
// Pur (pas de dépendance) : testable en node.

export const PARIS = { key: "paris", label: "Paris", lat: 48.8566, lon: 2.3522, radiusKm: 80 };

// Zones prestataires (planning matin) + grandes villes du moteur de prix.
export const ZONES = [
  { key: "cote_azur", label: "Côte d'Azur", lat: 43.7102, lon: 7.262, radiusKm: 90, words: /\b(nice|cannes|monaco|monte[- ]carlo|antibes|saint[- ]tropez|st[- ]tropez|menton|grasse|fr[ée]jus|mandelieu|sophia)\b|\b06\d{3}\b|\b83990\b|\b98000\b/i },
  { key: "marseille", label: "Marseille", lat: 43.2965, lon: 5.3698, radiusKm: 60, words: /\bmarseille|aix[- ]en[- ]provence|\bcassis\b|marignane|\b13\d{3}\b/i },
  { key: "lyon", label: "Lyon", lat: 45.764, lon: 4.8357, radiusKm: 60, words: /\blyon\b|saint[- ]exup[ée]ry|\b69\d{3}\b/i },
  { key: "bordeaux", label: "Bordeaux", lat: 44.8378, lon: -0.5792, radiusKm: 70, words: /\bbordeaux|m[ée]rignac|saint[- ][ée]milion|arcachon|\b33\d{3}\b/i },
  { key: "nantes", label: "Nantes", lat: 47.2184, lon: -1.5536, radiusKm: 80, words: /\bnantes\b|la baule|saint[- ]nazaire|\b44\d{3}\b/i },
  { key: "toulouse", label: "Toulouse", lat: 43.6047, lon: 1.4442, radiusKm: 60, words: /\btoulouse|blagnac|\b31\d{3}\b/i },
  { key: "montpellier", label: "Montpellier", lat: 43.6108, lon: 3.8767, radiusKm: 70, words: /\bmontpellier|\bn[îi]mes\b|\b34\d{3}\b|\b30\d{3}\b/i },
  { key: "strasbourg", label: "Strasbourg", lat: 48.5734, lon: 7.7521, radiusKm: 60, words: /\bstrasbourg|colmar|\b67\d{3}\b|\b68\d{3}\b/i },
  { key: "lille", label: "Lille", lat: 50.6292, lon: 3.0573, radiusKm: 60, words: /\blille\b|roubaix|tourcoing|\b59\d{3}\b/i },
  { key: "dijon", label: "Dijon", lat: 47.322, lon: 5.0415, radiusKm: 60, words: /\bdijon|beaune|\b21\d{3}\b/i },
  { key: "rennes", label: "Rennes", lat: 48.1173, lon: -1.6778, radiusKm: 80, words: /\brennes\b|saint[- ]malo|mont[- ]saint[- ]michel|\b35\d{3}\b/i },
  { key: "reims", label: "Reims", lat: 49.2583, lon: 4.0317, radiusKm: 50, words: /\breims\b|[ée]pernay|\b51\d{3}\b/i },
  { key: "caen", label: "Caen", lat: 49.1829, lon: -0.3707, radiusKm: 70, words: /\bcaen\b|deauville|honfleur|bayeux|\b14\d{3}\b/i },
  { key: "avignon", label: "Avignon", lat: 43.9493, lon: 4.8055, radiusKm: 40, words: /\bavignon|\b84\d{3}\b/i },
];

export const UNCLASSIFIED = { key: "autre", label: "À classer" };

const PARIS_WORDS = /\bparis\b|\bcdg\b|charles[- ]de[- ]gaulle|roissy|\borly\b|beauvais|le bourget|disney|chessy|versailles|la d[ée]fense|\b(75|77|78|91|92|93|94|95)\d{3}\b/i;

export function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function coordsOf(point) {
  const lat = Number(point && (point.lat ?? point.latitude));
  const lon = Number(point && (point.lon ?? point.lng ?? point.longitude));
  return Number.isFinite(lat) && Number.isFinite(lon) && (lat || lon) ? { lat, lon } : null;
}

/** Zone d'une course : { key, label }. Paris si départ OU arrivée à < 80 km de
 *  Paris ; sinon la ville la plus proche du DÉPART (dans son rayon, à défaut la
 *  plus proche à < 150 km) ; sinon le texte des adresses ; sinon « À classer ». */
export function zoneOf(booking) {
  const p = coordsOf(booking && booking.pickup), d = coordsOf(booking && booking.dropoff);
  const text = `${(booking && booking.pickup_address) || ""} | ${(booking && booking.dropoff_address) || ""}`;
  if (p || d) {
    const toParis = Math.min(...[p, d].filter(Boolean).map((c) => haversineKm(c.lat, c.lon, PARIS.lat, PARIS.lon)));
    if (toParis <= PARIS.radiusKm) return PARIS;
    const ref = p || d;
    let best = null;
    for (const z of ZONES) {
      const km = haversineKm(ref.lat, ref.lon, z.lat, z.lon);
      if (!best || km < best.km) best = { z, km };
    }
    if (best && (best.km <= best.z.radiusKm || best.km <= 150)) return best.z;
    return UNCLASSIFIED;
  }
  if (PARIS_WORDS.test(text)) return PARIS;
  for (const z of ZONES) if (z.words.test(text)) return z;
  return UNCLASSIFIED;
}
